import { format } from "date-fns";
import {
  calculateAge,
  formatDuration,
  formatMedicineStrength,
  getFrequencyText,
  getFullName,
} from "./utils";
import { formatPID } from "../../utils";

function getPatientName(name, gender, age) {
  const sep = "&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;";
  return name + sep + gender + sep + age;
}

function getFormattedDate(date) {
  return format(new Date(date), "dd LLL yyyy");
}

export function getPatientExaminationFindingsProcedureText(list) {
  if (!list?.length) return;
  return list
    ?.map((f) => {
      if (f.notes && f.notes.trim()) {
        return `${f.chip} - ${f.notes.trim()}`;
      }
      return f.chip;
    })
    .join(", ");
}

function getMedicinePx(px) {
  return px.medicines.map((medicine) => {
    return {
      name: (medicine.drugType ?? "") + " " + medicine.drugName,
      nameLabel: formatDuration(medicine.duration),
      strength: formatMedicineStrength(medicine.strength),
      frequency: medicine.frequency,
      instructions: medicine.frequencyPerWeek + " " + medicine.instruction,
      composition: medicine?.composition || "",
    };
  });
}
function getPatientDiagnosisText(diagnosis) {
  if (!diagnosis?.length) return "NA";

  return diagnosis
    .map(({ additionalInfo, diagnosis }) => {
      const description = additionalInfo?.description?.[0] || diagnosis;
      const note = additionalInfo?.note?.[0];
      return note ? `${description} - ${note}` : description;
    })
    .join(", ");
}

export function transformAdviceChipListToCommaSeperated(chipList) {
  if (!Array.isArray(chipList) || chipList === null || chipList.length === 0) {
    return ""; // Return an empty string if the input is invalid, null, or empty
  }

  const commaSeperatedAdvices = chipList
    ?.map((chip) => `${chip?.advice ? chip?.advice : chip?.chip}`) // Generate bullet point for each advice property
    ?.join(" "); // Join with new lines to form a paragraph

  return commaSeperatedAdvices;
}

function formatInstructions(text) {
  return text
    .split("\n") // Split the text into lines
    .filter((line) => line.trim() !== "") // Remove empty lines
    .map((line) => (line.trim().startsWith("•") ? line : `• ${line.trim()}`)); // Add bullet if missing
}

export const formatTestItem = (item) => {
  const ai = item?.additionalInfo || {};
  const isAvailable = ai?.type?.[0] === "Available";
  const result = ai?.result?.[0];
  const note = ai?.note?.[0];

  if (isAvailable) {
    // Prefer result, else fall back to note, else nothing
    if (result) return `${item.name} - ${result} (Result)`;
    if (note) return `${item.name} - ${note} (Result)`;
    return `${item.name} (Result)`;
  }

  // Order mode (or unspecified): show note if present
  if (note) return `${item.name} - ${note}`;
  return item.name;
};

export function formatTestsWithNote(tests, generalNote) {
  const testsText =
    Array.isArray(tests) && tests.length > 0
      ? Array.from(tests).map(formatTestItem).join(", ")
      : "";

  return [testsText, generalNote].filter(Boolean).join(". ");
}

export function getFormatedTestListWithNoteForPxComponent(tests, generalNote) {
  const processedTestList =
    Array.isArray(tests) && tests.length > 0
      ? Array.from(tests).map(formatTestItem)
      : [];

  return [...processedTestList, generalNote].filter(Boolean);
}

function getConsultationTypeText(consultationType) {
  if (
    consultationType == "" ||
    consultationType == null ||
    consultationType == undefined
  ) {
    return "NA";
  }
  return consultationType;
}

export function getModel(
  patientProfile,
  px,
  complaints,
  assessmentScoresSentence,
  findingsSentence
) {
  return {
    pFullName:
      patientProfile?.firstName +
      " " +
      patientProfile?.middleName +
      " " +
      patientProfile?.lastName,
    pGender: patientProfile?.gender,
    pAge: calculateAge(patientProfile.dob) + " yrs.",
    pId: formatPID(patientProfile.patientId),
    diagnosis:
      px?.diagnosisChips &&
      getPatientDiagnosisText(px.diagnosisChips?.diagnosis),
    patientComplaints: complaints,
    todaysNotes: [
      px?.complaintChips?.customNote,
      findingsSentence && findingsSentence,
      assessmentScoresSentence && assessmentScoresSentence,
    ]
      .filter(Boolean)
      .join("; "),

    consultDate: getFormattedDate(px.date),
    consultationType: getConsultationTypeText(px.consultationType),
    medicines: getMedicinePx(px),
    splInstructions: px?.instructions && formatInstructions(px.instructions),
    splAdvice:
      px?.adviceChips?.advices &&
      transformAdviceChipListToCommaSeperated(px?.adviceChips?.advices),
    followUpDate: px.followUpDate ? getFormattedDate(px.followUpDate) : "",
    pathologyTests: formatTestsWithNote(
      px?.pathologyChips?.investigations,
      px?.pathologyChips?.note
    ),
    bodyScanTests: formatTestsWithNote(
      px?.radiologyChips?.investigations,
      px?.radiologyChips?.note
    ),
    examinationFindings:
      px?.examinationChips?.examinations &&
      getPatientExaminationFindingsProcedureText(
        px?.examinationChips?.examinations
      ),
    procedures:
      px?.procedureChips?.procedures &&
      getPatientExaminationFindingsProcedureText(
        px?.procedureChips?.procedures
      ),

    clinicDetails: px.clinicMetadata,
    doctorDetails: px.doctorMetadata,
  };
}

const dummyMedicines = [
  {
    name: "Tablet Atrova",
    nameLabel: "20 days Tablet(s)",
    strength: "10 mg",
    frequency: [0, 0, 1],
    instructions: "",
  },
  {
    name: "Tablet Foltirax",
    nameLabel: "3 days Tablet(s)",
    strength: "2.5 mg",
    frequency: [0, 1, 0],
    instructions: "Monday",
  },
  {
    name: "Capsule Uprise",
    nameLabel: "1 days Tablet(s)",
    strength: "600000 IU",
    frequency: [1, 0, 0],
    instructions: "Once a month",
  },
];

export const dummyData = {
  pFullName: "Santosh Kumar",
  pGender: "Male",
  pAge: "58 yrs.",
  pId: "21-000-080",
  diagnosis: "Ankylosing Spondylitis",
  consultDate: "05 Feb 2023",
  consultType: "In person",
  medicines: dummyMedicines,
  splInstructions: [
    "For any emergency, contact casualty at Fortis Hospital Mulund (022 43654364), Bethany Hospital Thane (022 69115100), Godrej Hospital Vikhroli (022 66417100)",
    "For outstation, do consult your physician & keep us updated.",
    "Generic medicines may be taken from govt authorized shops.",
  ],
  splAdvice: [
    "Do not continue medicines beyond the prescribed date. They are potentially harmful without supervision.",
    "Do not plan pregnancy without informing in advance.",
    "In case of any unusual symptom with any medication; contact me / family physician immediately",
  ],
  followUpDate: "05 Mar 2023",
  pathologyTests: "CBC ESR, CRP, SGOT SGPT, Creat",
  bodyScanTests:
    "Thoracic spine, Knee bil AP standing, SI joint prone, Bilateral hands with wrist, Cervical spine- AP/lat",
};
